import React from "react";
import { Box, Stack, Typography, IconButton, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import etcStyles from "../../pages/css/etc.module.css"
import { DateTimePicker } from '../../components/DatePicker.js'
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

/* 설정 속성 */
const DIRECTION_ORDER = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
const SLOT_CONFIG = {
    /* 사거리 이하 슬롯 구성 */
    // 12시 방향 (북)
    top: {
        tyTop: '23%', tyLeft: '50%', labelPos: 'top',
        rotate: 0, tyTransform: 'translateX(-50%)',
        stTop: '1%', stLeft: '50%', stTransform: 'translateX(-50%)'
    },
    // 3시 방향 (동)
    right: {
        tyTop: '50%', tyLeft: '75%', labelPos: 'right',
        rotate: 90, tyTransform: 'translate(-50%, -50%) rotate(90deg)',
        stTop: '50%', stLeft: '64%', stTransform: 'translateY(-50%)'
    },
    // 6시 방향 (남)
    bottom: {
        tyTop: '70%', tyLeft: '50%', labelPos: 'bottom',
        rotate: 180, tyTransform: 'translateX(-50%)',
        stTop: '80%', stLeft: '50%', stTransform: 'translateX(-50%)'
    },
    // 9시 방향 (서)
    left: {
        tyTop: '50%', tyLeft: '25%', labelPos: 'left',
        rotate: -90, tyTransform: 'translate(-50%, -50%) rotate(-90deg)',
        stTop: '50%', stLeft: '-7%', stTransform: 'translateY(-50%)'
    },

    /* 오거리 슬롯 구성 */
    // 1시 방향 (북동)
    p01: { 
        tyTop: '32%', tyLeft: '65%',labelPos: 'top',
        rotate: 36, tyTransform: 'translate(-50%, -50%)',
        stTop: '16%', stLeft: '75%', stTransform: 'translate(-50%, -50%)'
    },
    // 4시 방향 (남동)
    p04: { 
        tyTop: '58%', tyLeft: '70%', labelPos: 'right',
        rotate: 110, tyTransform: 'translate(-50%, -50%)',
        stTop: '60%', stLeft: '82%', stTransform: 'translate(-50%, -50%)'
    },
    // 6시 방향 (남)
    p06: { 
        tyTop: '70%', tyLeft: '50%', labelPos: 'bottom',
        rotate: 180, tyTransform: 'translate(-50%, -50%)',
        stTop: '90%', stLeft: '50%', stTransform: 'translate(-50%, -50%)'
    },
    // 8시 방향 (남서)
    p08: { 
        tyTop: '58%', tyLeft: '30%', labelPos: 'left',
        rotate: -110, tyTransform: 'translate(-50%, -50%)',
        stTop: '60%', stLeft: '17%', stTransform: 'translate(-50%, -50%)'
    },
    // 11시 방향 (북서)
    p11: { 
        tyTop: '32%', tyLeft: '35%', labelPos: 'top',
        rotate: -36, tyTransform: 'translate(-50%, -50%)',
        stTop: '16%', stLeft: '25%', stTransform: 'translate(-50%, -50%)'
    }
};

/* 교통량 표시 컴포넌트 */
const TrafficPill = ({ value, direction = "row", type, parentRotate = 0, lightMode = false }) => {
    const arrowImgUrl = type === 'in' ? "/images/arrow_icon_02_blue.png" : "/images/arrow_icon_02_red.png";
    const relativeRotate = type === 'out' ? 180 : 0; 

    return (
        <Stack direction={direction} alignItems="center" spacing={0.5} 
            sx={{ px: 1, py: 0.5, borderRadius: 1, zIndex: 10, gap: '20px' }}>
            <Box 
                component="img" 
                src={arrowImgUrl} 
                alt="arrow"
                sx={{ width: 'auto', height: '32px', transform: `rotate(${relativeRotate}deg)` }}/>
            <Typography variant="body2" fontWeight="bold" sx={{ 
                color: lightMode ? '#333' : '#fff',
                fontSize:'13px', 
                bgcolor: lightMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(60, 60, 60, 0.5)', 
                borderRadius: '4px', 
                px: 0.6, 
                py: 0.3, 
                transform: `rotate(${-parentRotate}deg)`, 
                textAlign: 'center',
                minWidth: '62px'
            }}>
                {value ? value.toLocaleString() : 0}
            </Typography>
        </Stack>
    );
};

/*교차로 접근로별 교통량 오버레이 컴포넌트*/
export default function TrafficVolumeByCrsrd({ feature, onClose, data, onSearch, lightMode = false, initialConfig, onConfigChange }) {
    const yesterday = dayjs().subtract(1, 'day');
    const [viewType, setViewType] = React.useState(initialConfig.viewType);
    const [startDate, setStartDate] = React.useState(initialConfig.startDate);
    const [endDate, setEndDate] = React.useState(initialConfig.endDate);

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [headerNames, setHeaderNames] = React.useState({
        n: '북측', s: '남측', e: '동측', w: '서측'
    });

    // 뷰 타입 변경 핸들러
    const handleViewTypeChange = (event, newType) => {
        if (newType !== null && newType !== viewType) {
            setLoading(true);
            setViewType(newType);
        }
    };

    React.useEffect(() => {
        if (onConfigChange) {
            onConfigChange({ viewType, startDate, endDate });
        }
    }, [viewType, startDate, endDate]);

    // API 데이터 파싱 및 변환 함수
    const processApiData = React.useCallback((apiData) => {
        if (!apiData || !apiData.acsr || !apiData.values) return [];

        const idMap = {}; 
        const newHeaders = {};

        apiData.acsr.forEach(item => {
            let dir = '';
            if (item.acsrNm.includes('(북서)') || item.acsrNm.includes('(서북)')) dir = 'nw';
            else if (item.acsrNm.includes('(남서)') || item.acsrNm.includes('(서남)')) dir = 'sw';
            else if (item.acsrNm.includes('(북동)') || item.acsrNm.includes('(동북)')) dir = 'ne';
            else if (item.acsrNm.includes('(남동)') || item.acsrNm.includes('(동남)')) dir = 'se';
            else if (item.acsrNm.includes('(북)')) dir = 'n';
            else if (item.acsrNm.includes('(남)')) dir = 's';
            else if (item.acsrNm.includes('(동)')) dir = 'e';
            else if (item.acsrNm.includes('(서)')) dir = 'w';

            if (dir) {
                idMap[item.acsrId] = dir;
                newHeaders[dir] = item.acsrNm; 
            }
        });
        setHeaderNames(newHeaders);

        const groupedData = {};
        apiData.values.forEach(item => {
            const timeKey = item.tm; 
            if (!groupedData[timeKey]) {
                groupedData[timeKey] = {
                    timeFull: timeKey,
                    n_in: 0, n_out: 0,
                    s_in: 0, s_out: 0,
                    e_in: 0, e_out: 0,
                    w_in: 0, w_out: 0,
                    nw_in: 0, nw_out: 0,
                    sw_in: 0, sw_out: 0,
                    ne_in: 0, ne_out: 0,
                    se_in: 0, se_out: 0
                };
            }
            const dir = idMap[item.acsrId]; 
            if (dir) {
                groupedData[timeKey][`${dir}_in`] = item.inTrf || 0;
                groupedData[timeKey][`${dir}_out`] = item.outTrf || 0;
            }
        });

        const resultRows = Object.values(groupedData).map((row, index) => {
            const total = Object.keys(row).reduce((acc, key) => {
                if (key.endsWith('_in') || key.endsWith('_out')) return acc + row[key];
                return acc;
            }, 0);
            let timeLabel = "";
            if (viewType === 'daily') {
                timeLabel = dayjs(row.timeFull).format('M월 D일');
            } else {
                timeLabel = dayjs(row.timeFull).format('M월 D일 H시');
            }
            return {
                ...row,
                id: index,
                time: timeLabel,
                total: total
            };
        });

        resultRows.sort((a, b) => a.timeFull.localeCompare(b.timeFull));
        return resultRows;
    }, [viewType]);

    // 조회 핸들러
    const handleSearch = () => {
        if (!startDate || !endDate) return;
        
        if (startDate.isAfter(endDate)) {
            alert("시작일이 종료일보다 늦을 수 없습니다.");
            return;
        }

        if (endDate.isAfter(startDate.add(1, 'month'))) {
            alert("조회 기간은 최대 1달을 초과할 수 없습니다.");
            return;
        }
        const format = viewType === 'hourly' ? 'YYYYMMDDHH' : 'YYYYMMDD';
        const sdtm = startDate.format(format);
        const edtm = viewType === 'hourly' ? endDate.add(1, 'hour').format(format) : endDate.format(format);
        setLoading(true);
        if (onSearch) {
            onSearch(sdtm, edtm, viewType);
        }
    };

    // 다른 교차로 클릭 시 자동으로 조회 실행
    React.useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature, viewType]);

    // CSV 다운로드 핸들러
    const handleDownload = () => {
        if (rows.length === 0) {
            alert("다운로드할 데이터가 없습니다.");
            return;
        }
        const activeDirs = DIRECTION_ORDER.filter(dir => headerNames[dir]);
        const headerLabels = [
            "교차로명",
            viewType === 'hourly' ? "조회시간" : "조회일자",
            "합계"
        ];
        activeDirs.forEach(dir => {
            headerLabels.push(`${headerNames[dir]}_진입`);
            headerLabels.push(`${headerNames[dir]}_진출`);
        });

        const headerRow = headerLabels.join(",");
        const dataRows = rows.map(row => {
            const rowData = [
                feature.crsrdNm,
                row.time,
                row.total
            ];
            activeDirs.forEach(dir => {
                rowData.push(row[`${dir}_in`] || 0);
                rowData.push(row[`${dir}_out`] || 0);
            });

            return rowData.join(",");
        });
        const csvString = [headerRow, ...dataRows].join("\n");
        const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        let dateInfo = '';
        if (viewType === 'hourly') {
            dateInfo = `${startDate.format('YYYYMMDDHH')}~${endDate.format('YYYYMMDDHH')}`;
        } else {
            dateInfo = `${startDate.format('YYYYMMDD')}-${endDate.format('YYYYMMDD')}`;
        }

        link.setAttribute('download', `${feature.crsrdNm}_교통량_${dateInfo}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleRowClick = (params) => {
        setSelectedRow(params.row);
    };

    // 데이터 변경 감지
    React.useEffect(() => {
        if (!data || !data.values || data.values.length === 0) {
            return;
        }
        
        try {
            const formattedRows = processApiData(data);
            setRows(formattedRows);
            if (formattedRows.length > 0) {
                setSelectedRow(formattedRows[0]);
            }
        } catch (e) {
            console.error("데이터 처리 중 오류:", e);
        } finally {
            setLoading(false);
        }

    }, [data, processApiData]);

    const activeDirs = React.useMemo(() => {
        return DIRECTION_ORDER.filter(dir => headerNames[dir]);
    }, [headerNames]);
    const isPentagon = (data?.acsr?.length === 5) || (activeDirs.length === 5);
    const slots = React.useMemo(() => {
        if (isPentagon) {
            return {
                p01: activeDirs[0] || null, // 1시
                p04: activeDirs[1] || null, // 4시
                p06: activeDirs[2] || null, // 6시
                p08: activeDirs[3] || null, // 8시
                p11: activeDirs[4] || null  // 11시
            };
        } else {
            const used = new Set();
            const getDir = (candidates) => {
                const found = candidates.find(dir => activeDirs.includes(dir) && !used.has(dir));
                if (found) used.add(found);
                return found || null;
            };
            return {
                top: getDir(['n', 'ne', 'nw']),
                right: getDir(['e', 'se', 'ne']),
                bottom: getDir(['s', 'sw', 'se']),
                left: getDir(['w', 'nw', 'sw'])
            };
        }
    }, [activeDirs, isPentagon]);
    const bgImage = isPentagon ? 'intsec_bg_5.svg' : 'intsec_bg_0.7.svg';
    
    // 라이트 모드 스타일 정의
    const lightModeStyles = lightMode ? {
        pnlWrap: {
            backgroundColor: 'rgba(255, 255, 255, 0.95) !important',
            color: '#333 !important',
            border: '1px solid rgba(0, 0, 0, 0.1)',
        },
        pnlHeader: {
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            bgcolor: 'rgba(0, 0, 0, 0.02)'
        },
        pnlTit: {
            color: '#333 !important',
        },
        toggleButton: {
            bgcolor: lightMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255,255,255,0.1)',
            '& .MuiToggleButton-root': { 
                color: lightMode ? '#666' : '#ccc', 
                border: 'none', 
                py: 0.5, 
                fontSize: '12px',
                '&:hover': {
                    backgroundColor: lightMode ? 'rgba(83, 100, 255, 0.08)' : 'rgba(255,255,255,0.15)',
                }
            },
            '& .Mui-selected': { 
                bgcolor: '#5364FF', 
                color: 'white', 
                fontSize: '12px',
                '&:hover': {
                    backgroundColor: '#919cff',
                }
            }
        },
        closeButton: {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            color: '#333',
            '&:hover': { bgcolor: '#f5f5f5', color: '#000' },
        },
        intersectionBox: {
            bgcolor: '#f8f9fa', 
            border: '1px solid rgba(0, 0, 0, 0.1)'
        },
        dataGrid: {
            '& .MuiDataGrid-main': { color: '#333' },
            '& .MuiDataGrid-columnHeaders': { 
                bgcolor: '#f5f5f5', 
                color: '#333', 
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)' 
            },
            '& .MuiDataGrid-cell': { 
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                '&:focus': { outline: 'none' },
                '&:focus-within': { outline: 'none' }
            },
            '& .text-blue': { color: '#1976d2 !important' },
            '& .text-red': { color: '#d32f2f !important' },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgb(83 100 255 / 12%)',
            },
            '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'rgb(83 100 255 / 20%) !important',
                color: '#333 !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: 'rgb(83 100 255 / 30%) !important',
            },
            '& .MuiDataGrid-row.Mui-selected .text-blue': {
                color: '#1976d2 !important',
            },
            '& .MuiDataGrid-row.Mui-selected .text-red': {
                color: '#d32f2f !important',
            },
            '& .MuiDataGrid-virtualScroller': {
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-thumb': { 
                    bgcolor: 'rgba(0, 0, 0, 0.2)', 
                    borderRadius: '3px' 
                }
            }
        }
    } : {};

    // DataGrid 컬럼 정의
    const columns = React.useMemo(() => [
        { field: 'time', headerName: viewType === 'hourly' ? '시간' : '일자', width: 120, align: 'center', headerAlign: 'center' },
        { field: 'total', headerName: '합계', width: 70, align: 'center', headerAlign: 'center' },
        ...activeDirs.flatMap(dir => [
            { field: `${dir}_in`, headerName: '진입', width: 60, align: 'center', headerAlign: 'center', cellClassName: 'text-blue' },
            { field: `${dir}_out`, headerName: '진출', width: 60, align: 'center', headerAlign: 'center', cellClassName: 'text-red' }
        ])
    ], [viewType, activeDirs]);
    const columnGroupingModel = React.useMemo(() => {
        const groups = activeDirs.map(dir => ({
            groupId: dir, headerName: headerNames[dir], headerClassName: 'direction-header',
            children: [{ field: `${dir}_in` }, { field: `${dir}_out` }]
        }));

        return groups.filter(group => {
            const firstField = group.children[0].field;
            return columns.some(col => col.field === firstField);
        });
    }, [activeDirs, headerNames, columns]);


    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <div className={etcStyles.pnlWrap} style={{ 
                position: 'relative', 
                overflow: 'visible',
                ...(lightMode ? lightModeStyles.pnlWrap : {})
            }} elevation={0}>
                <IconButton 
                    onClick={onClose}
                    sx={{ 
                        position: 'absolute',
                        left: '-30px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '30px',
                        height: '60px',
                        borderRadius: '8px 0 0 8px',
                        zIndex: 1000,
                        ...(lightMode ? lightModeStyles.closeButton : {
                            bgcolor: '$rgba(50, 50, 50, 0.9)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'white',
                            '&:hover': { bgcolor: '#444' },
                        })
                    }}
                >
                <PlayArrowIcon 
                    sx={{ 
                        fontSize: '20px' 
                    }} 
                />
                </IconButton>
                {/* 헤더 */}
                <Stack className={etcStyles.pnlHeader} direction="row" alignItems="center" justifyContent="space-between" sx={lightMode ? lightModeStyles.pnlHeader : { p: 1, pl: 2, bgcolor: 'rgba(255,255,255,0.05)' }}>
                    <Typography className={etcStyles.pnlTit} fontWeight="bold" sx={lightMode ? lightModeStyles.pnlTit : {}}>{feature.crsrdNm}</Typography>
                    {/* 시간별/일별 선택 토글 */}
                    <ToggleButtonGroup
                        className={etcStyles.dayTglBtn}
                        value={viewType}
                        exclusive
                        onChange={handleViewTypeChange}
                        size="small"
                        sx={lightMode ? {
                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                            '& .MuiToggleButton-root': { 
                                color: '#666', 
                                border: 'none', 
                                py: 0.5, 
                                fontSize: '12px',
                                '&:hover': {
                                    backgroundColor: 'rgba(83, 100, 255, 0.08)',
                                }
                            },
                            '& .Mui-selected': { 
                                bgcolor: '#5364FF !important', 
                                color: 'white !important', 
                                fontSize: '12px',
                                '&:hover': {
                                    backgroundColor: '#919cff !important',
                                }
                            }
                        } : { 
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '& .MuiToggleButton-root': { color: '#ccc', border: 'none', py: 0.5,  fontSize:'12px' },
                            '& .Mui-selected': { bgcolor: '#5364FF', color: 'white', fontSize:'12px' }
                        }}
                    >
                        <ToggleButton value="hourly">시간별</ToggleButton>
                        <ToggleButton value="daily">일별</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {/* 컨트롤러 영역 */}
                <Stack className={etcStyles.pnlSrch}>
                    {/* 상단: 뷰 타입 및 조회 버튼 */}
                    <div  className="flx a_center gap10">
                        <div className={etcStyles.inputWrap} direction="row" alignItems="center" sx={{ minHeight: '32px' }}>
                            <DateTimePicker
                                mode={viewType === 'hourly' ? 60 : 24}
                                label="조회시작일"
                                value={startDate}
                                maxDate={yesterday.endOf('day')}
                                onChange={setStartDate}
                                lightMode={lightMode}
                                inputSx={{
                                    '& .MuiOutlinedInput-root input': {
                                    fontSize: '12px',
                                    padding: '6px 8px',
                                    color: lightMode ? '#333' : 'white',
                                    },
                                    /* 라벨 기본 상태 */
                                    '& .MuiInputLabel-root': {
                                    fontSize: '16px',
                                    transform: 'translate(14px, 14px) scale(1)',
                                    color: lightMode ? '#666' : 'white',
                                    },

                                    /* 포커스 or 값 있을 때 (위로 올라간 상태) */
                                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                    fontSize: '10px',
                                    transform: 'translate(14px, -6px) scale(0.9)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                    color: lightMode ? '#1976d2' : 'white',
                                    },
                                    '& .MuiSvgIcon-root': {
                                    fontSize: '18px',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: lightMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#5364FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#6C7AFC',
                                    },
                                    },
                                }}
                            />
                            <DateTimePicker
                                mode={viewType === 'hourly' ? 60 : 24} 
                                label="조회종료일"
                                value={endDate}
                                maxDate={yesterday.endOf('day')}
                                onChange={setEndDate}
                                lightMode={lightMode}
                                inputSx={{
                                    '& .MuiOutlinedInput-root input': {
                                    fontSize: '12px',
                                    padding: '6px 8px',
                                    color: lightMode ? '#333' : 'white',
                                    },
                                    /* 라벨 기본 상태 */
                                    '& .MuiInputLabel-root': {
                                    fontSize: '16px',
                                    transform: 'translate(14px, 14px) scale(1)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    /* 포커스 or 값 있을 때 (위로 올라간 상태) */
                                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                    fontSize: '10px',
                                    transform: 'translate(14px, -6px) scale(0.9)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                    color: lightMode ? '#1976d2' : 'white',
                                    },
                                    '& .MuiSvgIcon-root': {
                                    fontSize: '18px',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: lightMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#5364FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#6C7AFC',
                                    },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div  className={etcStyles.btnWrap}>
                            <Button variant="contained" size="small" onClick={handleSearch} sx={{ bgcolor: '#5364FF', color:'#fff', px: 0 }}>
                                <SearchIcon />
                            </Button>
                            <Button variant="outlined" size="small" onClick={handleDownload} sx={{ 
                                color: lightMode ? '#333' : '#ccc', 
                                borderColor: lightMode ? '#333' : '#ccc', 
                                px: 0,
                                '&:hover': {
                                    borderColor: lightMode ? '#000' : '#fff',
                                    color: lightMode ? '#000' : '#fff',
                                }
                            }}>
                                <FileDownloadIcon />
                            </Button>
                    </div>
                </Stack>
                {/* 이미지 영역 */}
                <div className={etcStyles.crsrdImgBox}>
                    <Box sx={{ 
                        width: '100%', 
                        aspectRatio: '1/1', 
                        borderRadius: '4px',
                        backgroundImage: `url('/images/${bgImage}')`, 
                        backgroundSize: '100% 100%', 
                        backgroundRepeat: 'no-repeat', 
                        backgroundPosition: 'center',
                        position: 'relative',
                        ...(lightMode ? lightModeStyles.intersectionBox : {
                            bgcolor: '#1e1e1e', 
                            border: '1px solid rgba(255,255,255,0.1)'
                        })
                    }}>
                        {selectedRow && (
                            // [교차로 렌더링]
                            Object.entries(slots).map(([slotKey, dir]) => {
                                if (!dir) return null;
                                const cfg = SLOT_CONFIG[slotKey];
                                
                                return (
                                    <React.Fragment key={slotKey}>
                                        {/* 라벨 (북측, 동측 등) */}
                                        <Typography sx={{ 
                                            position: 'absolute', top: cfg.tyTop, left: cfg.tyLeft, 
                                            transform: cfg.tyTransform, 
                                            whiteSpace: 'nowrap', fontSize: '14px',
                                            // fontWeight: 'bold', 
                                            // textShadow: lightMode ? '1px 1px 2px rgba(255,255,255,0.8)' : '1px 1px 2px black', 
                                            color: '#fff',
                                            // color: lightMode ? '#333' : '#fff',
                                            zIndex: 20 
                                        }}>
                                            {headerNames[dir]}
                                        </Typography>
                                        <Stack spacing={4} direction="row" sx={{ 
                                            position: 'absolute', top: cfg.stTop, left: cfg.stLeft, 
                                            transform: `${cfg.stTransform} rotate(${cfg.rotate}deg)`
                                        }}>
                                            <TrafficPill  value={selectedRow[`${dir}_in`]} direction="column-reverse" type="in" parentRotate={cfg.rotate} lightMode={lightMode} />
                                            <TrafficPill value={selectedRow[`${dir}_out`]} direction="column-reverse" type="out" parentRotate={cfg.rotate} lightMode={lightMode} />
                                        </Stack>
                                    </React.Fragment>
                                );
                            })
                        )}
                    </Box>
                </div>

                {/* 데이터 그리드 */}
                <div className={etcStyles.gridWrap}>
                    <DataGrid
                        key={`grid-${viewType}-${feature.crsrdNm}`}
                        rows={rows}
                        columns={columns}
                        onRowClick={handleRowClick}
                        columnGroupingModel={columnGroupingModel}
                        loading={loading}
                        density="compact"
                        rowHeight={36}
                        columnHeaderHeight={36}
                        experimentalFeatures={{ columnGrouping: true }}
                        hideFooterSelectedRowCount
                        sx={{
                            fontSize:'12px',
                            border: 'none',
                            height: '100%',
                            '& .MuiDataGrid-virtualScroller': {
                                overflow: 'auto !important',
                                '&::-webkit-scrollbar': { 
                                    width: '6px', 
                                    height: '6px'
                                },
                                '&::-webkit-scrollbar-thumb': { 
                                    bgcolor: lightMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)', 
                                    borderRadius: '4px' 
                                },
                                '&::-webkit-scrollbar-track': {
                                    bgcolor: lightMode ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
                                },
                                '&::-webkit-scrollbar-corner': { 
                                    bgcolor: lightMode ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'
                                }
                            },
                            '& .MuiDataGrid-footerContainer': {
                                '& *' : {  color: '#333' },
                                minHeight: '24px',
                                height: '24px',
                                overflow: 'hidden'
                            },
                            '& .MuiDataGrid-overlay': { backgroundColor: 'transparent' },
                            ...(lightMode ? lightModeStyles.dataGrid : {
                                '& .MuiDataGrid-main': { color: 'white' },
                                '& .MuiDataGrid-columnHeaders': { bgcolor: '#333', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.1)' },
                                '& .MuiDataGrid-columnHeaderTitleContainer': { justifyContent: 'center' },
                                '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(255,255,255,0.05)' },
                                '& .text-blue': { color: '#64b5f6 !important' },
                                '& .text-red': { color: '#ef5350 !important' }
                            })
                        }}
                    />
                </div>
            </div>
        </LocalizationProvider>
    );
}
